import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiClient } from '../lib/api';
import { local } from '../lib/storage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, obj = authed
  const [isPro, setIsPro] = useState(false);
  const [plan, setPlan] = useState('free');
  const [usage, setUsage] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);

  const refreshBusiness = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/businesses');
      const profile = data?.[0] || null;
      setBusinessProfile(profile);
      return profile;
    } catch {
      setBusinessProfile(null);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/auth/me');
      setUser(data);
      setIsPro(!!data.is_pro);
      setPlan(data.plan || (data.is_pro ? 'starter' : 'free'));
      setUsage(data.usage || null);
      await refreshBusiness();
    } catch {
      setUser(false);
      setIsPro(false);
      setPlan('free');
      setUsage(null);
      setBusinessProfile(null);
    }
  }, [refreshBusiness]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    local.clearAll();
    setUser(data.user);
    await refresh();
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await apiClient.post('/auth/register', { email, password, name });
    local.clearAll();
    setUser(data.user);
    await refresh();
    return data;
  };

  const loginWithGoogle = async (credential) => {
    const { data } = await apiClient.post('/auth/google', { credential });
    local.clearAll();
    setUser(data.user);
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await apiClient.post('/auth/logout'); } catch {}
    local.clearAll();
    setUser(false);
    setIsPro(false);
    setPlan('free');
    setUsage(null);
    setBusinessProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, isPro, plan, usage, businessProfile, login, register, loginWithGoogle, logout, refresh, refreshBusiness }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
